/**
 * CoTrainer Frontend — Bootstrap (Phase 3.5 §5 Shell-Refactor).
 *
 * Flow:
 *   1. /login/callback → Token aus Hash setzen + Hash-Cleanup
 *   2. bestimmeStartWeg (auth/start-weg.ts) → setCurrentUser
 *   3. Kein User → Login-Page / Profil-Zuordnung rendern (Shell wird NICHT gemountet)
 *      Störung (5xx, keine Verbindung) → Störungsseite, Anmeldung bleibt (seit 02.11.2026)
 *   4. User → mountShell + registerAllRoutes + startRouter
 *
 * Shell + Router rendert die View in `#view-container` (innerhalb der Shell).
 * Login-Page läuft außerhalb der Shell weil sie kein Login-Layout braucht.
 */

import "./styles/form-inputs.css";
import { handleOAuthCallback } from "./auth/callback";
import { bestimmeStartWeg } from "./auth/start-weg";
import { setToken } from "./auth/token";
import { mountShell } from "./components/app-shell";
import { setCurrentUser, startRouter } from "./router/router";
import { registerAllRoutes } from "./router/routes";
import { initDataBanner } from "./lib/data-banner";
import { renderLoginPage } from "./views/login-page";
import { renderPublicAuthPage } from "./views/auth-pages";
import { renderAuthClaimProfile } from "./views/auth-claim-profile";
import { renderStoerungPage } from "./views/stoerung-page";
import { appDateFormat, appOutcomeLabels } from "./api/client";
import { setAppDateFormat } from "./lib/dates";
import { setFeatureFlags } from "./lib/feature-flags";
import { setOutcomeLabels } from "./lib/outcome-labels";
import { createIcons, icons } from "lucide";

// HR-Views (Phase 8) nutzen Lucide-Icons via `data-l`-Attribut + globalem
// `lucide.createIcons`. Wir binden das komplette Icon-Set einmalig an `window`,
// sodass jede View nach ihrem `innerHTML` `lucide.createIcons({nameAttr:"data-l"})`
// aufrufen kann. (Bundle-Optimierung — nur genutzte Icons — später möglich.)
(window as unknown as { lucide: { createIcons: (o?: Record<string, unknown>) => void } }).lucide = {
  createIcons: (opts: Record<string, unknown> = {}) => createIcons({ icons, ...opts }),
};

// Public-Auth-Seiten (ohne Token erreichbar) — werden VOR probeAuth gerendert,
// da der Router erst nach Login läuft.
const PUBLIC_AUTH_PATHS = new Set(["/aktivieren", "/passwort-reset", "/passwort-vergessen"]);

async function bootstrap(): Promise<void> {
  const app = document.getElementById("app");
  if (!app) {
    console.error("#app Element nicht gefunden");
    return;
  }

  // 0. Public-Auth-Seite (Aktivieren/Reset/Vergessen)? Direkt rendern, ohne Auth.
  if (PUBLIC_AUTH_PATHS.has(window.location.pathname)) {
    renderPublicAuthPage(app, window.location.pathname);
    return;
  }

  // 1. Callback-Pfad? Token-Hash konsumieren
  const parsed = handleOAuthCallback();
  if (parsed) {
    setToken(parsed.token, parsed.exp);
  }

  // 2. UserContext probe → Startweg (auth/start-weg.ts).
  // 🔴 Seit 02.11.2026 mit eigenem Ausgang „gestört“: bei 5xx / keiner Verbindung
  // die Störungsseite statt der Anmeldemaske, und die Anmeldung bleibt liegen.
  // Bis dahin warf eine Störung hier die Anmeldung weg ⇒ Login-Schleife (29.10.).
  const start = await bestimmeStartWeg();
  setCurrentUser(start.weg === "app" ? start.user : null);

  if (start.weg === "stoerung") {
    renderStoerungPage(app, start.grund);
    return;
  }
  if (start.weg === "claim") {
    renderAuthClaimProfile(app, start.claim);
    return;
  }
  if (start.weg === "login") {
    renderLoginPage(app, { redirectTo: pendingTargetFromLocation() });
    return;
  }
  const user = start.user;

  // 2b. App-weite Anzeige-Settings parallel laden: Datumsformat (DATE_FORMAT;
  // Default "de") + Feature-Flags (LIGA_LIVE) + konfigurierbare Outcome-Labels
  // (M1–M8/F1–F9). Beide fail-safe — schlägt eines fehl, bleibt der Default.
  const [dateRes, labelsRes] = await Promise.allSettled([appDateFormat(), appOutcomeLabels()]);
  if (dateRes.status === "fulfilled") {
    setAppDateFormat(dateRes.value.date_format);
    setFeatureFlags({
      liga_live: dateRes.value.liga_live,
      arena_live: dateRes.value.arena_live,
      buchung_sichtbar: dateRes.value.buchung_sichtbar
    });
  }
  if (labelsRes.status === "fulfilled") setOutcomeLabels(labelsRes.value.outcome_labels);

  // ═══ KOMPAKT-TAB (Justin 16.08.) ══════════════════════════════════════════
  // > *„wichtig wäre auch für den Vertrieb, dass man die Buchungsseite separat
  // > nebenbei in einem Tab offen haben kann"*
  //
  // 🔑 **Kein zweiter Aufbau der Seite, sondern DIESELBE mit weniger Rahmen.**
  // Ein eigener Einstieg fürs Caller-Formular wäre ein zweites Formular mit
  // eigener Fehlerrate — genau der Befund aus ⑭ 14.1 A. `?tab=1` setzt nur eine
  // Klasse am `<body>`; Rail und Sub-Sidebar verschwinden per CSS, die Ansicht
  // bleibt Zeile für Zeile dieselbe.
  if (new URLSearchParams(window.location.search).get("tab") === "1") {
    document.body.classList.add("kompakt-tab");
  }

  // 3. Shell mounten + Router starten
  mountShell(app, user);
  registerAllRoutes();
  initDataBanner(); // R1 (E2): persistentes „Daten unvollständig"-Banner bei PARTIAL_DATA
  startRouter();
}

function pendingTargetFromLocation(): string | undefined {
  const p = window.location.pathname;
  if (!p || p === "/" || p === "/login/callback") return undefined;
  return p + window.location.search;
}

void bootstrap();
